import { Component, Host, JSX, h, Event, EventEmitter, Prop, State, Listen } from '@stencil/core'
import { container, ListingProduct, TranslationService } from '@rdss/client'
import { store, trans } from '~/store'
import { adobeTrackingSource } from '~/utils/tracking'

interface BrandSuggestion {
    id: number
    name: string
    shopUrl: string
    logoUrl?: string
    bannerUrl?: string
    _links: {
        self: {
            href: string
        }
    }
}

@Component({
    scoped: true,
    tag: 'zr-search-form',
    styleUrl: 'zr-search-form.scss',
})

/**
 * ZooRoyal search Formular component.
 */
export class ZrSearchForm {
    /**
     * Resolver for Translation service.
     */
    @Prop() readonly translationService = container.resolve(TranslationService)

    /**
     * Debounce time for search event.
     */
    @Prop() debounceTime = 500

    /**
     * Property to signal component the searching state.
     */
    @Prop() isSearching = false

    /**
     * Form action.
     */
    @Prop() action: string

    /**
     * The brand suggestion results.
     */
    @Prop({ mutable: true }) brandSuggestions: BrandSuggestion[] = []

    /**
     * The product suggestion results.
     */
    @Prop({ mutable: true }) productSuggestions: ListingProduct[] = []

    /**
     * The category for adobe search tracking.
     */
    @Prop() category = 'productsearch'

    /**
     * The search type for adobe search tracking.
     */
    @Prop() searchType = 'searchflyout_brand'

    /**
     * The number of suggestions shown.
     */
    @Prop() showNumberSuggestions = 4

    /**
     * The total number of search results (from pagination).
     */
    @Prop({ mutable: true }) totalResults = 0

    /**
     * The users last searches.
     */
    @Prop() userSearches = []

    /**
     * The minimum number of characters for search query.
     */
    @Prop() minSearchChars = 3

    /**
     * Is aria link selected.
     */
    @State() ariaSelected = false

    /**
     * The state that defines if the flyout is shown.
     */
    @State() showFlyout = false

    /**
     * The state that defines if there is a value.
     */
    @State() inputValue = ''

    /**
     * The state that defines if there is a value.
     */
    @State() hasInputFocus = false

    /**
     * Debounce Timeout.
     */
    @State() debounceTimeout: ReturnType<typeof setTimeout> | null = null

    /**
     * Input Change Event.
     */
    @Event() searchInputChange: EventEmitter<{value: string}>

    /**
     * The search element.
     */
    private inputElement: HTMLInputElement

    /**
     * The last search elements.
     */
    private lastSearchQueries: HTMLAnchorElement[] = []

    /**
     * The brand suggestion elements.
     */
    private brandSuggestionQueries: HTMLAnchorElement[] = []

    /**
     * The brand logo element.
     */
    private brandLogos: HTMLAnchorElement[] = []

    /**
     * The product elements.
     */
    private productSearchSuggestions: HTMLElement[] = []

    /**
     * The total results button.
     */
    private totalResultsButton: HTMLButtonElement

    /**
     * The last search query emitted.
     */
    private lastSearchEmitted = ''

    /**
     * Closes flyout when clicked outside search form and flyout.
     */
    @Listen('click', { target: 'document' })
    handleClickOutside(ev) {
        if((!ev.target.closest('.zr-search-form'))) {
            this.showFlyout = false
        }
    }

    /**
     * Closes flyout when blur outside search form and flyout.
     */
    @Listen('focusin', { target: 'document' })
    handleFlyoutBlur(ev) {
        if((!ev.target.closest('.zr-search-form'))) {
            this.showFlyout = false
        }
    }

    /**
     * Handle search form submit.
     *
     * @param {Event} event - The submit event to handle.
     */
    handleSubmit = (event: Event) => {
        if (this.inputValue.trim().length < this.minSearchChars) {
            event.preventDefault()
        } else {
            this.showFlyout = false
        }
    }

    /**
     * Emit Search Input Event.
     */
    private handleSearchInputChange = (event: Event) => {
        this.inputValue = this.inputElement.value
        if(['Space', 'ArrowDown', 'ArrowUp'].includes(event['code'])) {
            return
        }
        clearTimeout(this.debounceTimeout)
        if(event['code'] === 'Enter' && this.inputValue.trim().length >= this.minSearchChars) {
            this.inputElement.form?.submit()
            return
        }
        this.debounceTimeout = setTimeout(() => {
            if (this.inputElement.value) {
                if (this.inputElement.value !== this.lastSearchEmitted) {
                    this.lastSearchEmitted = this.inputElement.value
                    this.searchInputChange.emit({
                        value: this.inputElement.value.trim(),
                    })
                }
            } else {
                this.emptyOutInput()
            }
        }, this.debounceTime)
    }

    /**
     * Clear the search input field.
     */
    private emptyOutInput = () => {
        this.inputValue = ''
        this.lastSearchEmitted = ''
        this.totalResults = 0
        this.brandSuggestions = []
        this.productSuggestions = []
    }

    private handleSearchInputClear = () => {
        this.emptyOutInput()
        this.inputElement.focus()
    }

    /**
     * Handles the focus event on the search input field.
     */
    private handleInputFocus = () => {
        this.showFlyout = true
        this.hasInputFocus = true
    }

    /**
     * Handles the blur event on the search input field.
     */
    private handleInputBlur = () => {
        this.hasInputFocus = false
    }

    /**
     * Handles the focus event on the search button.
     */
    private handleButtonFocus = () => {
        this.hasInputFocus = true
    }

    /**
     * Fetch Translations for search input.
     */
    private async loadTranslations() {
        return this.translationService?.findOne('frontend/index/search')
            .then((data) => {
                store.state.translations = { ...store.state.translations, ...data }
            })
            .catch(e => console.error('Failed fetching loadTranslations', e))
    }

    /**
     * Component will load lifecycle event.
     */
    async componentWillLoad() {
        await this.loadTranslations()
    }

    /**
     * Component did render lifecycle event.
     */
    async componentDidRender() {
        this.productSearchSuggestions = Array.from(document.querySelectorAll('.zr-product-teaser'))
    }

    /**
     * Store the last search queries.
     */
    storeUserQueries(el: HTMLAnchorElement, index: number) {
        if(el) {
            this.lastSearchQueries[index] = el
        } else {
            this.lastSearchQueries = []
        }
    }

    /**
     * Store the brand suggestions.
     */
    storeBrandSuggestionQueries(el: HTMLAnchorElement, index: number) {
        if(el) {
            this.brandSuggestionQueries[index] = el
        }else {
            this.brandSuggestionQueries = []
        }
    }

    /**
     * Store the brand logo.
     */
    storeBrandLogo(el: HTMLAnchorElement, index: number) {
        if (el) {
            this.brandLogos[index] = el
        } else {
            this.brandLogos = []
        }
    }

    /**
     * Handle keyboard navigation.
     */
    @Listen('keydown')
    handleKeyNavigation(event: KeyboardEvent) {
        const brandSuggestion = []
        this.brandSuggestionQueries.forEach((el, index) => {
            brandSuggestion.push(el)
            if(this.brandLogos[index]) {
                brandSuggestion.push(this.brandLogos[index])
            }
        })

        const allQueries = [
            ...this.lastSearchQueries,
            ...brandSuggestion,
            ...this.productSearchSuggestions,
            ...(this.totalResultsButton ? [this.totalResultsButton] : []),
        ]

        if(event.key === 'Escape') {
            this.inputElement.focus()
        }

        if(event.key === 'ArrowDown') {
            console.log('ArrowDown', allQueries)
            event.preventDefault()
            if (allQueries.length > 0) {
                const targetIndex = allQueries.findIndex(el => el === event.target)
                if (targetIndex === -1) {
                    allQueries[0].focus()
                } else if (targetIndex < allQueries.length - 1) {
                    allQueries[targetIndex + 1].focus()
                } else if (targetIndex === allQueries.length - 1) {
                    allQueries[0].focus()
                }
            }
        }

        if(event.key === 'ArrowUp') {
            event.preventDefault()
            if (allQueries.length > 0) {
                const targetIndex = allQueries.findIndex(el => el === event.target)
                if (targetIndex === -1) {
                    allQueries[allQueries.length - 1].focus()
                } else if (targetIndex > 0) {
                    allQueries[targetIndex - 1].focus()
                } else if (targetIndex === 0) {
                    allQueries[allQueries.length - 1].focus()
                }
            }
        }
    }

    /**
     * Renders the component.
     *
     * @returns {JSX.Element} - The rendered component.
     */
    render(): JSX.Element {
        return (
            <Host>
                <div
                    class="zr-search-form">
                    <form
                        action={this.action}
                        method="get"
                        class={{
                            'zr-search-form__formular': true,
                            'flyout-visible': this.showFlyout,
                            'input-focus': this.hasInputFocus,
                        }}
                        onSubmit={this.handleSubmit}
                        role="search"
                    >
                        <input
                            class="zr-search-form__input"
                            aria-label={trans('frontend/index/search.searchInputAriaLabel', 'Sucheingabe')}
                            aria-controls="search-flyout"
                            aria-haspopup="listbox"
                            name="sSearch"
                            placeholder={trans('frontend/index/search.searchInputPlaceholder', 'Suche...')}
                            type="search"
                            inputmode="search"
                            ref={el => (this.inputElement = el)}
                            value={this.inputValue}
                            onFocus={this.handleInputFocus}
                            onBlur={this.handleInputBlur}
                            onKeyUp={e => this.handleSearchInputChange(e)}
                            onKeyDown={this.handleKeyNavigation.bind(this)}
                            autocomplete="off"
                        />
                        {this.inputValue.length > 0 &&
                            <button
                                type="reset"
                                class="zr-search-form__btn-reset"
                                aria-label={trans('frontend/index/search.resetButtonAriaLabel', 'Suchfeld zurücksetzen')}
                                onClick={this.handleSearchInputClear}
                                onFocus={this.handleButtonFocus}
                                onBlur={this.handleInputBlur}
                            >
                                {trans('frontend/index/search.Reset', 'Reset')}
                            </button>
                        }
                        <button
                            class="zr-search-form__btn-submit"
                            aria-label={trans('frontend/index/search.searchButtonAriaLabel', 'Suchtext absenden')}
                            type="submit"
                            onFocus={this.handleButtonFocus}
                            onBlur={this.handleInputBlur}
                        >

                            {trans('frontend/index/search.IndexSearchFieldSubmit', 'Suchen')}
                        </button>

                        <div id="search-flyout"
                            class="zr-search-form__flyout">

                            {this.isSearching && <vx-loading-spinner />}

                            {!this.isSearching &&
                                this.inputValue.length < 3 && this.userSearches.length > 0 && (
                                <section aria-labelledby="last-searches">
                                    <h2 id="last-searches"
                                        class="zr-search-form__list-header">
                                        {trans('frontend/index/search.searchLastSearchesHeader', 'Zuletzt gesucht')}
                                    </h2>
                                    <ul class="zr-search-form__suggestions"
                                        aria-label="Zuletzt gesucht"
                                        role="list">
                                        {this.userSearches.slice(0, this.showNumberSuggestions).map((search, index) => (
                                            <li id={`last-searches-${index}`}
                                                class="zr-search-form__suggestion-item"
                                            >
                                                <a
                                                    title={search}
                                                    href={`/search?sSearch=${encodeURIComponent(search)}`}
                                                    tabindex={-1}
                                                    ref={el => this.storeUserQueries(el, index)}
                                                >
                                                    {search}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {this.inputValue.length > 2 && [
                                this.brandSuggestions?.length > 0 && [
                                    <section aria-labelledby="product-recommandtions">
                                        <h2 id="product-recommadtions"
                                            class="zr-search-form__list-header">
                                            {trans('frontend/index/search.searchBrandRecommendationHeader', 'Marken')}
                                        </h2>
                                        <ul class="zr-search-form__suggestions"
                                            aria-label="Marken"
                                            role="list">
                                            {this.brandSuggestions?.slice(0, this.showNumberSuggestions).map((option, index) => (
                                                <li class="zr-search-form__suggestion-item">
                                                    <a
                                                        class="zr-search-form__suggestions-link"
                                                        title={option.name}
                                                        href={`/search?sSearch=${encodeURIComponent(option.name)}`}
                                                        innerHTML={option.name}
                                                        tabindex={-1}
                                                        ref={el => this.storeBrandSuggestionQueries(el, index)}
                                                    >
                                                    </a>
                                                    {(option.logoUrl &&
                                                                option.shopUrl &&
                                                                !['cz', 'at'].includes(location.hostname.split('.').pop())) &&
                                                            <a
                                                                class="zr-search-form__shop-link"
                                                                href={adobeTrackingSource({
                                                                    searchUrl: option.shopUrl,
                                                                    searchTerm: this.inputValue,
                                                                    searchType: this.searchType,
                                                                    category: this.category,
                                                                })}
                                                                ref={el => this.storeBrandLogo(el, index)}
                                                                title={option.name}
                                                                tabindex={-1}
                                                                aria-label="Zum Marken Shop"
                                                            ><img class="zr-search-form__suggestions-logo"
                                                                    src={option.logoUrl}
                                                                    alt={`${option.name} logo`}
                                                                /></a>
                                                    }
                                                </li>
                                            ))}
                                        </ul>
                                    </section>,
                                ],
                                this.productSuggestions?.length > 0 && [
                                    <section aria-labelledby="product-suggestions">
                                        <h2 id="product-suggestions"
                                            class="zr-search-form__list-header">
                                            {trans('frontend/index/search.searchListBestsellterHeader', 'Bestseller')}
                                        </h2>
                                        <ul class="zr-search-form__products"
                                            aria-label="Bestseller">
                                            {this.productSuggestions?.filter(product => product.name)
                                                .slice(0, this.showNumberSuggestions)
                                                .map(((product, index) => (
                                                    <li id={`product-suggestions-${index}`}
                                                        class="zr-search-form__product-item">
                                                        <zr-product-teaser
                                                            searchTerm={this.inputValue}
                                                            product={product}
                                                            context='search-flyout'
                                                            class='zr-product-tile--search-flyout'
                                                        />
                                                    </li>
                                                )))}
                                        </ul>
                                    </section>,
                                ] || !this.isSearching && (
                                    <div class="zr-search-form__no-results">
                                        {trans('frontend/index/search.searchNoResults',
                                            'Leider konnten wir keine Produkte für deine Suche finden.')}
                                    </div>
                                ),
                                this.totalResults > this.showNumberSuggestions && (
                                    <div class="zr-search-form__results-button-container">
                                        <button
                                            class="zr-search-form__results-button"
                                            aria-label={
                                                trans(
                                                    'frontend/index/search.searchShowAllResultsAriaLabel',
                                                    'Alle Ergebnisse anzeigen'
                                                )
                                            }
                                            tabindex={-1}
                                            type="submit"
                                            ref = {el => (this.totalResultsButton = el)}
                                        >{
                                                trans('frontend/index/search.searchShowAllResults',
                                                    'Alle Ergebnisse anzeigen')
                                            } ({this.totalResults})
                                        </button>
                                    </div>
                                ),
                            ]}

                            {(this.brandSuggestions?.length > 0 && this.brandSuggestions[0].bannerUrl) &&
                                <div class="zr-search-form__suggestions-banner-container">
                                    <a
                                        href={this.brandSuggestions[0].shopUrl}
                                        title={this.brandSuggestions[0].name}
                                        tabindex={-1}
                                    ><img
                                            class="zr-search-form__suggestions-banner"
                                            loading="lazy"
                                            src={this.brandSuggestions[0].bannerUrl}
                                            alt={`${this.brandSuggestions[0].name} banner`}
                                        /></a>
                                </div>
                            }
                        </div>
                    </form>
                </div>
            </Host>
        )
    }
}
